import type { ApiErrorI, ApiExistingTicketErrorI } from 'declarations/interfaces/errors';

const DEFAULT_MAX_DETAIL_LENGTH = 280;

function collapseAdjacentDuplicates(parts: readonly string[]): string[] {
  const out: string[] = [];
  for (const p of parts) {
    if (out.length === 0 || out[out.length - 1] !== p) {
      out.push(p);
    }
  }
  return out;
}

function isGenericOperationCanceledMessage(s: string): boolean {
  return /^The operation was canceled\.?$/i.test(s.trim());
}

function isTransportAbortNoise(s: string): boolean {
  const t = s.trim();
  if (
    /^The I\/O operation has been aborted because of either a thread exit or an application request\.?$/i.test(
      t
    )
  ) {
    return true;
  }
  return (
    /Unable to read data from the transport connection:/i.test(t) &&
    /I\/O operation has been aborted/i.test(t)
  );
}

function summarizeIfDotNetHttpTimeout(parts: readonly string[]): string | null {
  const text = parts.join(' ');
  if (!/HttpClient\.Timeout|timeout.*elapsing|seconds elapsing/i.test(text)) {
    return null;
  }
  const m = text.match(/(\d+)\s*seconds?/i);
  const seconds = m?.[1];
  if (seconds != null) {
    return `Timed out after ${seconds}s (server limit). Try smaller files or add them from ticket details.`;
  }
  return 'Timed out (server limit). Try smaller files or add them from ticket details.';
}

/**
 * Shortens API ProblemDetails `detail` strings that embed .NET exception chains (` -> `)
 * or repeat generic cancellation / I/O noise.
 */
function normalizeErrorDetail(detail: string, maxLen = DEFAULT_MAX_DETAIL_LENGTH): string {
  const trimmed = detail.trim();
  if (!trimmed) {
    return '';
  }

  const pipeHead = trimmed.split(' | ')[0]?.trim() ?? trimmed;
  const chainParts = pipeHead
    .split(/\s*->\s*/)
    .map(p => p.trim())
    .filter(Boolean);
  const deduped = collapseAdjacentDuplicates(chainParts);

  const timeoutSummary = summarizeIfDotNetHttpTimeout(deduped);
  if (timeoutSummary != null) {
    return timeoutSummary;
  }

  const kept = deduped.filter(
    s => !isGenericOperationCanceledMessage(s) && !isTransportAbortNoise(s)
  );
  let chosen = kept[0] ?? deduped[0] ?? pipeHead;
  chosen = chosen.replace(/\s*\.{2,}\s*$/, '.').trim();

  if (chosen.length > maxLen) {
    return `${chosen.slice(0, Math.max(0, maxLen - 1))}…`;
  }
  return chosen;
}

export const getErrorMessage = (error: ApiErrorI | null): string | undefined => {
  const detail = error?.data?.detail;
  if (detail == null || String(detail).trim() === '') {
    if (error?.code === 'ECONNABORTED' || error?.code === 'ERR_CANCELED') {
      return 'Request timed out or was canceled.';
    }
    return undefined;
  }

  const firstPipe = String(detail).trim().split(' | ')[0]?.trim() ?? String(detail).trim();
  const normalized = normalizeErrorDetail(firstPipe);
  return normalized === '' ? undefined : normalized;
};

export const getValidationErrorMessage = (error: ApiErrorI | null): string | undefined =>
  error?.data?.errors && Object.values(error?.data?.errors).flat(1).join('. ');

export const getExistingId = (error: ApiExistingTicketErrorI | null): string | null => {
  return error?.data?.ExistingId ? error?.data?.ExistingId : null;
};
